import { Spinner } from "@chakra-ui/react";
import { lazy, Suspense, useEffect } from "react";
import { useActiveAccount } from "applesauce-react/hooks";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";

import GlobalStyles from "./styles";

import { ErrorBoundary } from "./components/error-boundary";
import AppLayout from "./components/layout";
import useSetColorMode from "./hooks/use-set-color-mode";
import { RouteProviders } from "./providers/route";
import { hapticLight } from "./helpers/haptics";
import { UpdateBanner } from "./components/update-prompt";

import TaskManagerProvider from "./views/task-manager/provider";

// one off views
import NoteFoundView from "./views/404";
import HomeView from "./views/home";
import NostrLinkView from "./views/link";
import SearchView from "./views/search";
import ThreadView from "./views/thread";

const CommunitiesView = lazy(() => import("./views/communities"));

// Default view: redirect to messages when logged in, landing page when not
function DefaultView() {
  const account = useActiveAccount();
  if (account) return <Navigate to="/messages" replace />;
  return <HomeView />;
}

const HashTagView = lazy(() => import("./views/hashtag"));
const JoinView = lazy(() => import("./views/join"));

// routes
import groupsRoutes from "./views/groups/routes";
import messagesRoutes from "./views/messages/routes";
import newRoutes from "./views/new/routes";
import notificationsRoutes from "./views/notifications/routes";
import settingsRoutes from "./views/settings/routes";
import signinRoutes from "./views/signin/routes";
// signup removed — only private key sign-in is supported
import userRoutes from "./views/user/routes";
import gamesRoutes from "./views/games/routes";
import marketsRoutes from "./views/markets/routes";
import walletRoutes from "./views/wallet/routes";

// Redirect old hash routing
const hashPath = window.location.hash.match(/^#(\/.+)/);
if (hashPath) window.history.replaceState({}, "", hashPath[1]);

const RootPage = () => {
  useSetColorMode();

  return (
    <RouteProviders>
      <AppLayout />
    </RouteProviders>
  );
};

const NoLayoutPage = () => {
  return (
    <RouteProviders>
      <Outlet />
    </RouteProviders>
  );
};

export const router = createBrowserRouter(
  [
    { path: "*", Component: NoteFoundView },
    {
      path: "signin",
      Component: NoLayoutPage,
      children: signinRoutes,
    },
    {
      path: "join",
      Component: NoLayoutPage,
      children: [{ index: true, Component: JoinView }],
    },
    {
      Component: RootPage,
      children: [
        { index: true, Component: DefaultView },
        { path: "feed", Component: HomeView },
        { path: "new", children: newRoutes },
        { path: "notes", Component: HomeView },
        { path: "communities", Component: CommunitiesView },
        { path: "messages", children: messagesRoutes },
        { path: "user/:pubkey", children: userRoutes },
        { path: "u/:pubkey", children: userRoutes },
        { path: "note/:id", Component: ThreadView },
        { path: "n/:id", Component: ThreadView },
        { path: "search", Component: SearchView },
        { path: "settings", children: settingsRoutes },
        { path: "notifications", children: notificationsRoutes },
        { path: "groups", children: groupsRoutes },
        { path: "l/:link", Component: NostrLinkView },
        { path: "t", Component: HashTagView },
        { path: "t/:hashtag", Component: HashTagView },

        { path: "games", children: gamesRoutes },
        { path: "markets", children: marketsRoutes },
        { path: "wallet", children: walletRoutes },
      ],
    },
  ],
  { future: { v7_relativeSplatPath: true } },
);

export const App = () => {
  useEffect(() => {
    // Skip on desktop (no touch screen)
    if (!("ontouchstart" in window)) return;
    const handleHaptic = (e: TouchEvent) => {
      const target = (e.target as HTMLElement).closest(
        "button, a, [role='button'], [role='menuitem'], .chakra-menu__menuitem, .chakra-tabs__tab, .chakra-switch, .chakra-checkbox, .chakra-radio",
      );
      if (target) hapticLight();
    };
    document.addEventListener("touchstart", handleHaptic, { capture: true, passive: true });
    return () => document.removeEventListener("touchstart", handleHaptic, { capture: true });
  }, []);

  return (
    <ErrorBoundary>
      <GlobalStyles />
      <TaskManagerProvider parentRouter={router}>
        <Suspense fallback={<Spinner />}>
          <RouterProvider router={router} />
        </Suspense>
      </TaskManagerProvider>
      <UpdateBanner />
    </ErrorBoundary>
  );
};
